import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { ASSETS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { FleurDivider } from "@/components/Ornament";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.35]);

  return (
    <section id="hero" className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
      <motion.div
        style={{ y: bgY, backgroundImage: `url(${ASSETS.hero})` }}
        className="absolute inset-0 bg-cover bg-center scale-110 hidden md:block"
        data-testid="hero-background"
      />
      <motion.div
        style={{ y: bgY, backgroundImage: `url(${ASSETS.heroMobile})` }}
        className="absolute inset-0 bg-cover bg-center scale-110 md:hidden"
        data-testid="hero-background-mobile"
      />
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(6,24,18,0.4)_0%,rgba(6,24,18,0.05)_40%,rgba(6,24,18,0.35)_68%,rgba(6,24,18,0.85)_88%,#061812_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_45%_at_50%_38%,transparent,rgba(6,24,18,0.28))]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-4 pt-32 pb-16"
      >
        <h1
          className="font-display font-bold text-white leading-[1.04] text-4xl sm:text-5xl lg:text-7xl drop-shadow-[0_6px_30px_rgba(0,0,0,0.6)]"
          data-testid="hero-headline"
        >
          <Line delay={0.65}>ДВА СВІТИ.</Line>
          <Line delay={0.85}>
            <span className="text-gold-gradient">ОДНА ТУРБОТА.</span>
          </Line>
        </h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
        >
          <FleurDivider id="ornHero" className="mt-7" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-9 flex flex-col sm:flex-row items-center gap-3 sm:gap-4"
        >
          <button
            type="button"
            onClick={() => scrollToId("sadochky")}
            className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] font-semibold px-7 py-3.5 text-sm sm:text-base shadow-[0_0_30px_rgba(230,184,92,0.5)] hover:shadow-[0_0_45px_rgba(230,184,92,0.75)] transition-shadow duration-300 cursor-pointer"
            data-testid="hero-choose-button"
          >
            Обрати садочок
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            type="button"
            onClick={() => scrollToId("forma")}
            className="group flex items-center gap-2 rounded-full border border-amber-300/50 text-amber-100 px-7 py-3.5 text-sm sm:text-base hover:bg-amber-300/10 hover:border-amber-300 transition-colors duration-300 cursor-pointer"
            data-testid="hero-meet-button"
          >
            Записатися на знайомство
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>

        <motion.button
          type="button"
          onClick={() => scrollToId("sadochky")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2 }}
          className="mt-14 flex flex-col items-center gap-1.5 text-amber-200/60 hover:text-amber-300 transition-colors cursor-pointer"
          data-testid="hero-scroll-hint"
          aria-label="Гортати вниз"
        >
          <span className="text-[10px] tracking-[0.3em] font-cinzel uppercase">Гортайте вниз</span>
          <ChevronDown className="w-4 h-4 animate-float-soft" />
        </motion.button>
      </motion.div>
    </section>
  );
}
